import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { ShoppingCartIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDashboardFilters } from "../lib/dashboard-filters";
import { ProductAnalyticsItem } from "@babackapp/private-api-client/types";

/**
 * Props for the ProductSelectSearch component
 * @property {Function} setProductFilter - Function to set the product filter
 * @property {Function} onFilteredProductsChange - Optional callback when filtered products change
 * @property {ProductAnalyticsItem[]} products - Array of products to search through
 */
type ProductSelectSearchProps = {
  setProductFilter: (
    productId: string | undefined,
    variantId: string | undefined,
  ) => void;
  onFilteredProductsChange?: (products: ProductAnalyticsItem[]) => void;
  products?: ProductAnalyticsItem[];
};

/**
 * A component that provides a search input for filtering products
 * It displays the selected product if one is selected, or a search input otherwise
 * When a product is selected, it shows a button to clear the selection
 * When the search input changes, it filters the products and notifies the parent component
 */
export function ProductSelectSearch({
  setProductFilter,
  onFilteredProductsChange,
  products = [],
}: ProductSelectSearchProps) {
  const t = useTranslation();
  const [search, setSearch] = useState("");

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { productId, variantId } = useDashboardFilters();

  // Find selected product
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === productId) ?? null,
    [products, productId],
  );

  const selectedVariant = useMemo(
    () =>
      selectedProduct?.variants?.find((v) => v.variantId === variantId) ?? null,
    [selectedProduct, variantId],
  );

  /**
   * Calculate filtered products based on search input
   * This is memoized to avoid unnecessary recalculations
   * Returns an empty array if a product is already selected or search is empty
   * Otherwise, returns up to 50 products that match the search term, sorted by count
   */
  const filteredProducts = useMemo(() => {
    // Don't filter if a product is already selected or search is empty
    if (selectedProduct || !search.trim()) {
      return [];
    }
    
    const searchLower = search.toLowerCase();
    // Avoid creating a new array unnecessarily
    return products
      .filter(
        (product) =>
          product.title.toLowerCase().includes(searchLower) ||
          (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
          product.variants?.some((variant) =>
            variant.title.toLowerCase().includes(searchLower),
          ),
      )
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, 50);
  }, [search, products, selectedProduct]);
  

  /**
   * Handle search input changes and notify parent component
   * Uses debouncing to avoid too frequent updates
   * Includes error handling to prevent crashes
   * @param e - The input change event
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        // Safely get value from event
        const value = e?.target?.value || "";
        setSearch(value);

        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Skip debounce if no callback is provided
        if (!onFilteredProductsChange) {
          return;
        }

        // Use debounce to avoid too frequent updates
        debounceTimerRef.current = setTimeout(() => {
          try {
            // Don't filter if a product is already selected
            if (selectedProduct) {
              onFilteredProductsChange([]);
              return;
            }
            
            // If search is empty, reset the filtered products
            if (!value.trim()) {
              onFilteredProductsChange([]);
              return;
            }
            
            // Otherwise, notify parent with filtered products
            onFilteredProductsChange(filteredProducts);
          } catch (error) {
            // Log error but don't crash the component
            console.error("Error in search debounce callback:", error);
            // Try to reset to a safe state
            onFilteredProductsChange([]);
          }
        }, 150);
      } catch (error) {
        // Log error but don't crash the component
        console.error("Error handling search change:", error);
        // Try to reset to a safe state
        if (onFilteredProductsChange) {
          onFilteredProductsChange([]);
        }
      }
    },
    [selectedProduct, onFilteredProductsChange, filteredProducts],
  );

  /**
   * Handle clearing the product selection
   * Resets the product filter, search input, and filtered products
   * Includes error handling to prevent crashes
   */
  const handleClearSelection = useCallback(() => {
    try {
      // Clear product selection
      setProductFilter(undefined, undefined);
      // Reset search input
      setSearch("");
      // Reset filtered products in parent component
      if (onFilteredProductsChange) {
        onFilteredProductsChange([]);
      }
    } catch (error) {
      // Log error but don't crash the component
      console.error("Error clearing selection:", error);
      // Try individual operations to ensure at least partial reset
      try {
        setProductFilter(undefined, undefined);
      } catch (e) {
        console.error("Failed to reset product filter:", e);
      }
      
      try {
        setSearch("");
      } catch (e) {
        console.error("Failed to reset search:", e);
      }
    }
  }, [setProductFilter, onFilteredProductsChange]);

  const selectedValueText = selectedProduct
    ? selectedVariant
      ? `${selectedProduct.title} - ${selectedVariant.title}`
      : selectedProduct.title
    : null;
    
  /**
   * Side effects:
   * 1. Reset search when a product is selected
   * 2. Clean up debounce timer on unmount
   * Includes error handling to prevent crashes
   */
  useEffect(() => {
    try {
      // Reset search when a product is selected
      if (selectedProduct) {
        setSearch("");
      }
    } catch (error) {
      // Log error but don't crash the component
      console.error("Error in product selection effect:", error);
    }
    
    // Cleanup function for the debounce timer
    return () => {
      try {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      } catch (error) {
        // Log error but don't crash the component during cleanup
        console.error("Error cleaning up debounce timer:", error);
      }
    };
  }, [selectedProduct]);



  return (
    <div>
      {selectedValueText ? (
        <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-2 py-2 text-sm">
          <div className="flex gap-2 items-center truncate">
            {selectedProduct?.image ? (
              <img
                src={selectedProduct.image}
                alt={`Product image: ${selectedProduct.title}`}
                className="w-6 h-6 rounded-sm object-cover"
                loading="lazy"
                aria-hidden="false"
              />
            ) : (
              <div className="w-6 h-6 rounded-sm bg-muted-foreground/20 flex items-center justify-center shrink-0">
                <ShoppingCartIcon className="w-3 h-3 text-muted-foreground/50" />
              </div>
            )}
            <span className="truncate">{selectedValueText}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleClearSelection}
            aria-label="Clear product selection"
            title="Clear product selection"
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("productSelect.placeholder")}
            onChange={handleSearchChange}
            className="w-full"
            value={search}
            autoComplete="off"
            aria-label="Search products"
            aria-autocomplete="list"
            id="product-search-input"
          />
        </div>
      )}
    </div>
  );
}
